import {
  AfterContentInit,
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
  viewChild
} from '@angular/core';
import {
  RdsAutocompleteComponent,
  RdsAutocompleteHighlightDirective,
  RdsAutocompleteOptionDirective,
  RdsButtonModule,
  RdsEmptyStateModule,
  RdsFormFieldModule,
  RdsIconComponent,
  RdsLinkComponent,
  RdsPopoverModule
} from "@rds/angular-components";
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MinifiedProfile} from "../activity-detail-view.component";
import {AddExternalProfileActions, ExternalProfile} from "../../../models/external-profile.model";
import {AutocompletionOptionsService} from "../../../services/autocompletion-options.service";
import {map, Observable} from "rxjs";
import {Study} from "../../../models/metadata";
import {DetailedViewObjectService} from "../../../services/detailed-view-object.service";

@Component({
  selector: 'app-dynamic-activity-autocompletion',
  standalone: true,
  imports: [
    RdsAutocompleteComponent,
    RdsButtonModule,
    RdsFormFieldModule,
    RdsIconComponent,
    ReactiveFormsModule,
    RdsAutocompleteOptionDirective,
    RdsAutocompleteHighlightDirective,
    RdsEmptyStateModule,
    RdsPopoverModule,
    RdsLinkComponent
  ],
  templateUrl: './dynamic-activity-autocompletion.component.html',
  styleUrl: './dynamic-activity-autocompletion.component.scss'
})
export class DynamicActivityAutocompletionComponent<T = ExternalProfile | Study, R = MinifiedProfile | Study> implements OnChanges, AfterContentInit, OnDestroy {
  type = input.required<'profile' | 'study'>()
  excludeOptions = input<R[]>([])
  startingValue = input<T>()
  selection = output<T | undefined>()
  autocomplete = viewChild.required<RdsAutocompleteComponent<T>>("autocomplete")
  options: ((query: string) => Observable<T[]>) | T[] = []
  private readonly formBuilder = inject(FormBuilder)
  form!: FormGroup<{ selection: FormControl<T | null>, veevaURL: FormControl<string | null> }>
  profileType = input<'participant' | 'lead'>()
  veevaURLSubmitIsLoading = false

  constructor(private autocompleteOptionsService: AutocompletionOptionsService, private detailedViewObjectService: DetailedViewObjectService) {
  }

  setupForm(){
    const ep = this.startingValue() || null

    // @ts-ignore
    this.form = this.formBuilder.group({
      selection: [ep],
      veevaURL: ["", this.veevaURLValidator]
    })

    this.form.controls.selection.valueChanges.subscribe((value) => {
      if(value)
        this.submit()
    })

    this.form.controls.veevaURL.markAsTouched()
  }

  veevaURLValidator(control: FormControl<string | null>){
    const url = control.value
    const pIDRegex = /-[a-zA-Z0-9]+$/

    if(url && (!url.startsWith("https://veeva.link/p/") || !pIDRegex.test(url))){
      return {invalidVeevaURL: true}
    }
    return null
  }

  veevaURLSubmit(){
    if(this.form.controls.veevaURL.value && !this.form.controls.veevaURL.errors){
      this.veevaURLSubmitIsLoading = true
      this.detailedViewObjectService.postVeevaLinkProfileURL(this.form.controls.veevaURL.value).subscribe(res => {
        this.veevaURLSubmitIsLoading = false
        if(res.actionPerformed !== AddExternalProfileActions.ERROR){
          this.submit(res.addedPerson)
        }
        else{
          this.form.controls.veevaURL.setErrors({profileUnavailable: true})
        }
      })
    }
  }

  private adaptToType(){
    switch(this.type()){
      case 'study':
        // @ts-ignore
        this.options = this.getOptionsWrapper(this.autocompleteOptionsService.getStudies.bind(this.autocompleteOptionsService))
        // @ts-ignore
        break;
      case 'profile':
        // @ts-ignore
        this.options = this.getOptionsWrapper(this.autocompleteOptionsService.getExternalProfiles.bind(this.autocompleteOptionsService))
        if(!this.profileType()){
          throw new Error("Profile type is required when the profile autocompletion is selected")
        }
        break;
      default:
        this.options = []
        break;
    }
  }

  getOptionsWrapper(getOptions: ((query: string) => Observable<T[]>)){
    const excludeOptions = this.excludeOptions() || []
    return (query: string) => getOptions(query).pipe(
      map(options => options.filter(option => !excludeOptions.find(exclude => {
        if((option as ExternalProfile).eID){
          return (exclude as MinifiedProfile).eID === (option as ExternalProfile).eID
        }
        else if((option as Study).sID){
          return (exclude as Study).sID === (option as Study).sID
        }
        else return false
      })))
    )
  }

  submit(addedProfile?: ExternalProfile){
    const selectedValue = addedProfile || this.form.controls.selection.value
    // @ts-ignore
    this.selection.emit(selectedValue?.eID || selectedValue?.sID ? selectedValue : undefined)
  }

  abort(){
    this.selection.emit(undefined)
  }

  addNew(query: string, fetchedCollection: any[]){
    return fetchedCollection[0];
  }

  displayProperty(option: T) {
    if((option as ExternalProfile).eID) {
      const profile = option as ExternalProfile
      return `${profile.lastName}, ${profile.firstName}`
    }
    if((option as Study).sID){
      const study = option as Study
      return `${study.studyNumber} ${study.studyName? study.studyName : ''}`
    }
    else return ""
  }

  uniqProperty(option: T) {
    if((option as ExternalProfile).eID){
      return (option as ExternalProfile).eID!
    }
    if((option as Study).sID){
      return (option as Study).sID!
    }
    else return ""
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['startingValue']){
      this.setupForm()
    }
    if(changes["type"]) {
      this.adaptToType()
    }
  }

  ngAfterContentInit(): void {
    this.setupForm()
  }

  ngOnDestroy() {
    this.autocomplete().hide()
  }
}
